import React, { useRef, useEffect, useState, useMemo } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import CatalogHeader from "./CatalogHeader";
import rooms from "./rooms.json";
import RoomSelectionModal from "./RoomSelectionModal";

const RoomView: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [roomImages, setRoomImages] = useState<string[]>([]);
  const [showRoomModal, setShowRoomModal] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);
  let imgURL =
    queryParams.get("imgURL") ||
    "https://ik.imagekit.io/byuyzknw6/textures/1.jpg";
  const roomId = queryParams.get("room") || "1";

  const fetchRoomTextures = async (roomId: string) => {
    const formattedRoomId = `room${roomId}`;
    const room = rooms.find(
      (room: { roomId: string }) => room.roomId === formattedRoomId
    );
    if (room && room.images) {
      setRoomImages(room.images);
    } else {
      console.error("No images found for the specified room.");
    }
  };

  useEffect(() => {
    fetchRoomTextures(roomId);
  }, [roomId]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const wallConfig = {
    wallPosition: [
      {
        name: "frontWall",
        position: { x: 0, y: 10000, z: 0 },
        rotation: { y: 0 },
      },
      {
        name: "backWall",
        position: { x: 0, y: 300, z: -75 },
        rotation: { y: Math.PI },
      },
      {
        name: "leftWall",
        position: { x: -100, y: 30, z: 3 },
        rotation: { y: Math.PI / 2 },
      },
      {
        name: "rightWall",
        position: { x: 80, y: 39.1, z: 0 },
        rotation: { y: -Math.PI / 2 },
      },
    ],
    height: 100,
    width: 200,
    url: "https://ik.imagekit.io/byuyzknw6/portland-bone-matt-600x600mm.jpg?updatedAt=1742970769931",
  };

  const floorConfig = useMemo(
    () => ({
      name: "Floor",
      face: "floor",
      width: 1500,
      height: 1500,
      "position-x": 0,
      "position-y": -101,
      "position-z": 0,
      "rotation-x": Math.PI / 2,
      girdCountOnFloor: 20,
      url: imgURL,
      gridPlanWidth: 1500,
      gridPlanHeight: 1500,
      gridColor: "#000000",
      girdLineWidth: 0.3,
      gridScale: 6,
      textureUrls: roomImages.length === 6 ? roomImages : [],
    }),
    [imgURL, roomImages]
  );

  useEffect(() => {
    if (!canvasRef.current || floorConfig.textureUrls.length !== 6) return;

    const scene = new THREE.Scene();
    const fov = 42;
    const aspect = window.innerWidth / window.innerHeight;
    const near = 0.1;
    const far = 1000;
    const camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
    camera.position.set(0, 0, 0.5);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
    });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setClearColor(0x000000, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.minPolarAngle = Math.PI / 4;
    controls.maxPolarAngle = Math.PI / 2;
    controls.enableRotate = true;
    controls.rotateSpeed = -0.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.04;

    const loader = new THREE.TextureLoader();

    // Floor
    const tileTexture = loader.load(floorConfig.url);
    tileTexture.colorSpace = THREE.SRGBColorSpace;
    tileTexture.wrapS = THREE.RepeatWrapping;
    tileTexture.wrapT = THREE.RepeatWrapping;
    tileTexture.repeat.set(30, 30);
    tileTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();

    const floorGeometry = new THREE.PlaneGeometry(
      floorConfig.width,
      floorConfig.height
    );
    const floorMaterial = new THREE.MeshBasicMaterial({
      map: tileTexture,
      side: THREE.DoubleSide,
      transparent: false,
      opacity: 1,
      depthWrite: false,
      depthTest: false,
    });

    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.position.set(
      floorConfig["position-x"],
      floorConfig["position-y"],
      floorConfig["position-z"]
    );
    floor.rotation.x = floorConfig["rotation-x"];
    scene.add(floor);

    // Walls
    const wallTexture = loader.load(wallConfig.url);
    wallTexture.colorSpace = THREE.SRGBColorSpace;
    wallTexture.wrapS = THREE.RepeatWrapping;
    wallTexture.wrapT = THREE.RepeatWrapping;
    wallTexture.repeat.set(10, 10);

    const wallMaterial = new THREE.MeshBasicMaterial({
      map: wallTexture,
      side: THREE.DoubleSide,
      transparent: true,
    });

    const wallGeometry = new THREE.PlaneGeometry(
      wallConfig.width,
      wallConfig.height
    );
    wallConfig.wallPosition.forEach((wall) => {
      const wallMesh = new THREE.Mesh(wallGeometry, wallMaterial);
      wallMesh.position.set(wall.position.x, wall.position.y, wall.position.z);
      wallMesh.rotation.y = wall.rotation.y;
      scene.add(wallMesh);
    });

    // Grid Floor
    const gridCanvas = createGridCanvas(
      floorConfig.gridPlanWidth,
      floorConfig.gridPlanHeight,
      floorConfig.girdCountOnFloor
    );
    const gridTexture = new THREE.CanvasTexture(gridCanvas);
    const gridGeometry = new THREE.PlaneGeometry(
      floorConfig.gridPlanWidth,
      floorConfig.gridPlanHeight
    );
    const gridMaterial = new THREE.MeshBasicMaterial({
      map: gridTexture,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 1,
    });
    const gridPlane = new THREE.Mesh(gridGeometry, gridMaterial);
    gridPlane.position.set(
      floorConfig["position-x"],
      floorConfig["position-y"],
      floorConfig["position-z"]
    );
    gridPlane.rotation.x = floorConfig["rotation-x"];
    scene.add(gridPlane);

    // Skybox
    const textureArray: THREE.MeshBasicMaterial[] = [];
    let loadedCount = 0;

    floorConfig.textureUrls.forEach((url, index) => {
      loader.load(url, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.LinearMipMapLinearFilter;
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();

        textureArray[index] = new THREE.MeshBasicMaterial({
          map: texture,
          side: THREE.BackSide,
          transparent: true,
        });

        loadedCount++;
        if (loadedCount === 6) {
          const cubeGeometry = new THREE.BoxGeometry(100, 100, 100);
          const skyBox = new THREE.Mesh(cubeGeometry, textureArray);
          scene.add(skyBox);
          setIsLoading(false);
        }
      });
    });

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line
  }, [floorConfig]);

  const createGridCanvas = (
    width: number,
    height: number,
    divisions: number
  ): HTMLCanvasElement => {
    const canvas = document.createElement("canvas");
    const scale = floorConfig.gridScale;
    canvas.width = width * scale;
    canvas.height = height * scale;

    const context = canvas.getContext("2d");
    if (!context) return canvas;

    context.scale(scale, scale);
    context.clearRect(0, 0, width, height);
    context.strokeStyle = floorConfig.gridColor;
    context.lineWidth = floorConfig.girdLineWidth;

    const stepX = width / divisions;
    const stepY = height / divisions;

    for (let x = 0; x <= width; x += stepX) {
      context.moveTo(x, 0);
      context.lineTo(x, height);
    }

    for (let y = 0; y <= height; y += stepY) {
      context.moveTo(0, y);
      context.lineTo(width, y);
    }

    context.stroke();
    return canvas;
  };
  const handle3dRoomViewClick = (roomKey: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("room", roomKey);
    window.location.href = url.toString();
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", width: "100%", height: "100vh" }}
    >
      <CatalogHeader
        showSearch={false}
        showCatalogButton={false}
        showFullscreenbutton={true}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
        customButton={
          <button
            type="button"
            className="btn uniform-back-btn d-flex align-items-center justify-content-center"
            onClick={() => setShowRoomModal(true)}
          >
            Change room
          </button>
        }
      />
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgb(0, 0, 0)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            color: "#fff",
          }}
        >
          <Spinner animation="border" variant="light" />
        </div>
      )}
      <canvas className="web-gl" ref={canvasRef} />
      <RoomSelectionModal
        show={showRoomModal}
        onClose={() => setShowRoomModal(false)}
        onRoomSelect={handle3dRoomViewClick}
      />
    </div>
  );
};

export default RoomView;
